import { useState, useRef, useEffect } from 'react';

const AD_QUEST_WAIT_TIME_MS = 10000; // Час очікування після перегляду реклами (10 секунд)

// Глобальні змінні, які не скидаються при unmount компонента (зберігаються між переходами між екранами)
const globalProgressStartTimes = new Map<number, number>();
const globalAdProgress = new Map<number, number>();
const globalWatchedAdQuestIds = new Set<number>();
const globalAdProgressIntervals = new Map<number, ReturnType<typeof setInterval>>();

interface UseAdQuestProgressReturn {
  adQuestProgress: Map<number, number>;
  watchedAdQuestIds: Set<number>;
  startProgressBar: (userQuestId: number, onComplete?: () => void) => void;
  clearProgress: () => void;
}

/**
 * Хук для управління progress bar анімацією для квестів з рекламою
 * Використовує глобальні змінні для збереження стану між переходами між екранами
 */
export function useAdQuestProgress(isOpen: boolean): UseAdQuestProgressReturn {
  const [adQuestProgress, setAdQuestProgress] = useState<Map<number, number>>(new Map(globalAdProgress));
  const [watchedAdQuestIds, setWatchedAdQuestIds] = useState<Set<number>>(new Set(globalWatchedAdQuestIds));
  // Локальні інтервали для цього екземпляра компонента (очищаються при unmount)
  const localIntervalsRef = useRef<Map<number, ReturnType<typeof setInterval>>>(new Map());

  const startProgressBar = (userQuestId: number, onComplete?: () => void) => {
    // Очищаємо попередній інтервал для цього квесту, якщо він є (і з локального, і з глобального)
    const existingLocalInterval = localIntervalsRef.current.get(userQuestId);
    if (existingLocalInterval) {
      clearInterval(existingLocalInterval);
      localIntervalsRef.current.delete(userQuestId);
    }
    const existingGlobalInterval = globalAdProgressIntervals.get(userQuestId);
    if (existingGlobalInterval) {
      clearInterval(existingGlobalInterval);
      globalAdProgressIntervals.delete(userQuestId);
    }
    
    // Починаємо прогрес-бар з 0%
    globalAdProgress.set(userQuestId, 0);
    setAdQuestProgress(new Map(globalAdProgress));
    
    // Зберігаємо час старту в глобальну змінну
    const startTime = Date.now();
    globalProgressStartTimes.set(userQuestId, startTime);
    
    // Запускаємо таймер для оновлення прогрес-бару
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(100, (elapsed / AD_QUEST_WAIT_TIME_MS) * 100);
      
      // Оновлюємо глобальний стан
      globalAdProgress.set(userQuestId, progress);
      setAdQuestProgress(new Map(globalAdProgress));
      
        // Коли прогрес досягне 100%, позначаємо квест як завершений
        if (progress >= 100) {
          clearInterval(interval);
          localIntervalsRef.current.delete(userQuestId);
          globalAdProgressIntervals.delete(userQuestId);
          globalProgressStartTimes.delete(userQuestId);
          globalAdProgress.delete(userQuestId);
          globalWatchedAdQuestIds.add(userQuestId);
          setAdQuestProgress(new Map(globalAdProgress));
          setWatchedAdQuestIds(new Set(globalWatchedAdQuestIds));
          
          // Викликаємо callback після завершення, якщо він переданий
          if (onComplete) {
            onComplete();
          }
        }
    }, 50); // Оновлюємо кожні 50ms для плавної анімації
    
    // Зберігаємо інтервал в глобальну змінну та локальний ref
    localIntervalsRef.current.set(userQuestId, interval);
    globalAdProgressIntervals.set(userQuestId, interval);
  };

  const clearProgress = () => {
    // Очищаємо тільки локальні інтервали при закритті (але зберігаємо startTime в globalProgressStartTimes)
    // Глобальні інтервали продовжують працювати для оновлення globalAdProgress
    localIntervalsRef.current.forEach((interval) => {
      clearInterval(interval);
    });
    localIntervalsRef.current.clear();
    // НЕ скидаємо adQuestProgress та globalProgressStartTimes - вони зберігаються для показу при повторному відкритті
  };

  // Очищаємо інтервали прогрес-бару при закритті попапу (але зберігаємо прогрес)
  useEffect(() => {
    if (!isOpen) {
      clearProgress();
    }
  }, [isOpen]);

  // При відкритті модалки - відновлюємо progress bar для квестів, які ще не завершилися
  useEffect(() => {
    // Same Map instance for the hook lifetime; capture so cleanup does not re-read .current.
    const localIntervals = localIntervalsRef.current;

    if (isOpen) {
      // (c) Re-hydrate React state from module-level maps that survive unmount across screens
      // eslint-disable-next-line react-hooks/set-state-in-effect -- external global progress store
      setAdQuestProgress(new Map(globalAdProgress));
      setWatchedAdQuestIds(new Set(globalWatchedAdQuestIds));
      
      // Перевіряємо всі квести з globalProgressStartTimes
      globalProgressStartTimes.forEach((startTime, userQuestId) => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(100, (elapsed / AD_QUEST_WAIT_TIME_MS) * 100);
        
        // Очищаємо старий локальний інтервал для цього квесту, якщо він є
        const existingLocalInterval = localIntervals.get(userQuestId);
        if (existingLocalInterval) {
          clearInterval(existingLocalInterval);
          localIntervals.delete(userQuestId);
        }
        
        if (progress < 100) {
          // Прогрес ще не завершений - продовжуємо анімацію
          globalAdProgress.set(userQuestId, progress);
          setAdQuestProgress(new Map(globalAdProgress));
          
          // Запускаємо інтервал для продовження анімації
          const interval = setInterval(() => {
            const currentElapsed = Date.now() - startTime;
            const currentProgress = Math.min(100, (currentElapsed / AD_QUEST_WAIT_TIME_MS) * 100);
            
            // Оновлюємо глобальний стан
            globalAdProgress.set(userQuestId, currentProgress);
            setAdQuestProgress(new Map(globalAdProgress));
            
            if (currentProgress >= 100) {
              clearInterval(interval);
              localIntervals.delete(userQuestId);
              globalAdProgressIntervals.delete(userQuestId);
              globalProgressStartTimes.delete(userQuestId);
              globalAdProgress.delete(userQuestId);
              globalWatchedAdQuestIds.add(userQuestId);
              setAdQuestProgress(new Map(globalAdProgress));
              setWatchedAdQuestIds(new Set(globalWatchedAdQuestIds));
            }
          }, 50);
          
          localIntervals.set(userQuestId, interval);
          globalAdProgressIntervals.set(userQuestId, interval);
        } else {
          // Прогрес вже завершений - видаляємо з глобальних змінних
          globalProgressStartTimes.delete(userQuestId);
          globalAdProgress.delete(userQuestId);
          globalWatchedAdQuestIds.add(userQuestId);
          setAdQuestProgress(new Map(globalAdProgress));
          setWatchedAdQuestIds(new Set(globalWatchedAdQuestIds));
        }
      });
    }
    
    // Cleanup: очищаємо тільки локальні інтервали при розмонтуванні (глобальні залишаються)
    return () => {
      localIntervals.forEach((interval) => {
        clearInterval(interval);
      });
      localIntervals.clear();
    };
  }, [isOpen]);

  return {
    adQuestProgress,
    watchedAdQuestIds,
    startProgressBar,
    clearProgress,
  };
}
